import CartApi from '@apis/cart/CartApi';
import cartQueries from '@apis/queries/cartQueries';
import shopQueries from '@apis/queries/shopQueries';
import useCurrency from '@hooks/item/useCurrency';
import useCalculatedCart from '@store/cart/hooks/useCalculatedCart';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { orderLineToGtmFormat, pushEcommerceEvent } from '@utils/gtm';
import { useRef } from 'react';
import { toast } from 'sonner';

interface Props {
    shopId: string;
    cartId?: string;
}

function useBulkUpdateCartQuantity({ shopId, cartId }:Props) {
    const { data: cartData } = useQuery(cartQueries.detail(shopId, cartId!));
    const { orderLines } = useCalculatedCart();
    const queryClient = useQueryClient();
    const pendingRef = useRef<Promise<void> | null>(null);
    const currency = useCurrency();
    const { data: shop } = useQuery(shopQueries.detail(shopId));

    const bulkUpdate = async () => {
        if (!cartId || !cartData) {
            return;
        }
        // 수량에 변경이 있는 경우
        const changedLines = orderLines.filter((it) => {
            const cartItem = cartData.items
                .find((ci) => ci.cartItemId === it.cartItemId)!;
            return cartItem?.qty !== it.quantity;
        });
        // 전역 상태에서 삭제된 경우
        const deletedItems = cartData.items
            .filter((ci) => !orderLines.some((it) => it.cartItemId === ci.cartItemId));
        let isDeleteContains = deletedItems.length > 0;

        // 변경 사항에 대해 한번에 수행
        const apiCalls: Promise<never>[] = changedLines.map((it) => {
            if (it.quantity <= 0) {
                isDeleteContains = true;
                pushEcommerceEvent('remove_from_cart', {
                    value: it.cartItemId,
                    currency,
                    items: [orderLineToGtmFormat({ orderLine: it, shop })],
                });
                return CartApi.deleteCartItem(shopId, cartId, it.cartItemId!);
            }
            return CartApi.updateCartItemQuantity(shopId, cartId, it.cartItemId!, {
                qty: it.quantity,
            });
        });
        deletedItems.forEach((ci) => {
            apiCalls.push(CartApi.deleteCartItem(shopId, cartId, ci.cartItemId));
        });

        try {
            await Promise.all(apiCalls);
            if (isDeleteContains) {
                queryClient.resetQueries({ queryKey: cartQueries._def });
            } else {
                queryClient.invalidateQueries({ queryKey: cartQueries._def });
            }
        } catch (e) {
            console.error(e);
            toast.error('장바구니 수량을 변경하지 못했어요. 잠시 후 다시 시도해 주세요.');
        }
    };

    // 진행 중인 요청이 있으면 새로 호출하지 않고 해당 요청을 공유
    return () => {
        if (!pendingRef.current) {
            pendingRef.current = bulkUpdate().finally(() => {
                pendingRef.current = null;
            });
        }
        return pendingRef.current;
    };
}

export default useBulkUpdateCartQuantity;
